import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X, Sparkles } from "lucide-react";

const DISMISS_KEY = "aura_install_dismissed";
const DISMISS_HOURS = 72;

export function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    // Check if user recently dismissed
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (raw) {
        const at = parseInt(raw, 10);
        if (Date.now() - at < DISMISS_HOURS * 3600 * 1000) return;
      }
    } catch (e) {
      console.warn("install: localStorage unavailable", e);
    }

    // Standalone already? Don't show.
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) return;

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    // iOS Safari doesn't fire beforeinstallprompt — show manual hint after a short delay
    if (isIOS) {
      const t = setTimeout(() => {
        setIosHint(true);
        setVisible(true);
      }, 15000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBIP);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", onBIP);
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {}
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    setVisible(false);
    if (choice.outcome !== "accepted") {
      try {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      } catch {}
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-50 bg-[#131518] border border-[#26282C] rounded-lg p-4 shadow-2xl fade-in"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-sm bg-[#D5FF3F] flex items-center justify-center flex-shrink-0">
          <span className="font-display italic text-[#0B0C0E] text-xl leading-none">a</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-[#F5F3EE] text-sm">Install Aura Credit</div>
          <p className="text-xs text-[#A8ADA5] mt-1 leading-relaxed">
            {iosHint ? (
              <>
                Tap <span className="text-[#D5FF3F]">Share</span> → <span className="text-[#D5FF3F]">Add to Home Screen</span> to install.
              </>
            ) : (
              <>Add Aura to your home screen. Full-screen, offline-ready, one-tap.</>
            )}
          </p>
          {!iosHint && (
            <div className="flex gap-2 mt-3">
              <Button
                onClick={install}
                data-testid="pwa-install-btn"
                className="rounded-full btn-lime h-8 px-4 text-xs"
              >
                <Download size={12} className="mr-1.5" /> Install
              </Button>
              <Button
                onClick={dismiss}
                data-testid="pwa-dismiss-btn"
                className="rounded-full btn-outline h-8 px-4 text-xs"
              >
                Later
              </Button>
            </div>
          )}
        </div>
        <button
          onClick={dismiss}
          data-testid="pwa-close-btn"
          className="text-[#6D716C] hover:text-[#F5F3EE] p-1"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
